import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Button, CircularProgress, Alert, Grid, Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, Avatar, Divider, TextField, useTheme, useMediaQuery
} from '@mui/material';
import axiosInstance from '../../utils/axios';
import { getImageUrl, compressImages } from '../../utils/imageUtils';
import { RootState } from '../../store';
import SignatureCanvas from 'react-signature-canvas';
import CloseIcon from '@mui/icons-material/Close';

interface Week {
  id: string;
  weekStartDate: string;
  isCompleted: boolean;
  comments: Comment[];
}

interface Comment {
  id: string;
  content: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  photos?: string[];
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const ScheduledServiceDetail: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const responsibleId = (location.state && (location.state as any).responsibleId) || '';
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<Week | null>(null);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [photosToUpload, setPhotosToUpload] = useState<File[]>([]);
  const [evaluation, setEvaluation] = useState<{ [key: string]: string }>({});
  const [signature, setSignature] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const [evaluationFields, setEvaluationFields] = useState<string[]>([]);
  const sigCanvasRef = useRef<SignatureCanvas>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const backendUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'http://localhost:3000';



  useEffect(() => {
    fetchWeeks();
  }, [id]);

  const fetchWeeks = async (selectedWeekId?: string) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/scheduled-services/${id}/weeks`);
      setWeeks(res.data);
      if (selectedWeekId) {
        const found = res.data.find((w: Week) => w.id === selectedWeekId);
        setSelectedWeek(found || res.data[0] || null);
      } else {
        setSelectedWeek(res.data[0] || null);
      }
    } catch (err) {
      alert('Error al cargar semanas');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationFields = async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`/scheduled-services/${id}`);
      const categoryId = res.data.categoryId;
      const catRes = await axiosInstance.get(`/categories/${categoryId}`);
      setEvaluationFields(catRes.data.evaluationFields || []);
    } catch (err) {
      setEvaluationFields([]);
    }
  };

  const handleSelectWeek = (week: Week) => {
    setSelectedWeek(week);
    setNewComment('');
    setPhotosToUpload([]);
  };

  const handleAddComment = async () => {
    if (!selectedWeek || (!newComment.trim() && photosToUpload.length === 0)) {
      alert('Debes escribir un comentario o seleccionar al menos una foto.');
      return;
    }

    const formData = new FormData();
    formData.append('content', newComment);
    
    // Comprimir imágenes antes de subir
    try {
      const compressedPhotos = await compressImages(photosToUpload);
      compressedPhotos.forEach(photo => {
        formData.append('photos', photo);
      });
    } catch (error) {
      console.error('Error comprimiendo imágenes:', error);
      // Fallback a originales
      photosToUpload.forEach(photo => {
        formData.append('photos', photo);
      });
    }

    try {
      setLoading(true);
      await axiosInstance.post(`/scheduled-services/weeks/${selectedWeek.id}/comments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setNewComment('');
      setPhotosToUpload([]);
      fetchWeeks(selectedWeek?.id);
    } catch (err) {
      alert('Error al agregar comentario');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWeek = async () => {
    if (!selectedWeek) return;
    try {
      await axiosInstance.post(`/scheduled-services/weeks/${selectedWeek.id}/complete`);
      fetchWeeks(selectedWeek.id);
    } catch (err) {
      alert('Error al completar semana');
    }
  };

  const handleCompleteWeekWithEvaluation = async () => {
    if (!selectedWeek) return;
    try {
      await axiosInstance.post(`/scheduled-services/weeks/${selectedWeek.id}/complete`, {
        evaluation,
        signature
      });
      setOpenModal(false);
      fetchWeeks(selectedWeek.id);
    } catch (err) {
      alert('Error al completar semana');
    }
  };

  const handleOpenModal = async () => {
    await fetchEvaluationFields();
    setOpenModal(true);
    setEvaluation({});
    setSignature('');
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const canComplete = () => {
    if (!selectedWeek || selectedWeek.isCompleted) return false;
    const firstPending = weeks.find(w => !w.isCompleted);
    if (!firstPending || firstPending.id !== selectedWeek.id) return false;
    if (selectedWeek.comments.length === 0) return false;
    if (
      user?.role === 'DIRECTOR' ||
      user?.role === 'MANAGER' ||
      user?.id === responsibleId
    ) return true;
    return false;
  };

  const handleEvaluationChange = (field: string, value: string) => {
    setEvaluation({ ...evaluation, [field]: value });
  };

  const handleSaveSignature = () => {
    if (sigCanvasRef.current) {
      const dataUrl = sigCanvasRef.current.getCanvas().toDataURL('image/png');
      setSignature(dataUrl);
    }
  };

  const handleClearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
      setSignature('');
    }
  };

  return (
    <Box p={3}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>Volver</Button>
      <Typography variant="h5" gutterBottom>Detalle del Servicio Programado</Typography>
      {/* Responsive: mobile muestra lista o detalle, desktop ambos */}
      {isMobile ? (
        <>
          {/* Si no hay semana seleccionada, mostrar lista */}
          {!selectedWeek ? (
            <Paper sx={{ minWidth: 260, maxHeight: 600, overflow: 'auto', mb: 2 }}>
              <List>
                {weeks.map((week) => (
                  <ListItem
                    key={week.id}
                    button
                    selected={(selectedWeek as Week | null)?.id === week.id}
                    onClick={() => handleSelectWeek(week)}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <ListItemText
                      primary={`Semana del Lunes ${formatDate(week.weekStartDate)}`}
                    />
                    <Chip
                      label={week.isCompleted ? 'Completada' : 'Pendiente'}
                      color={week.isCompleted ? 'success' : 'warning'}
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          ) : (
            <Box>
              <Button variant="outlined" onClick={() => setSelectedWeek(null)} sx={{ mb: 2 }}>Volver a semanas</Button>
              {/* Detalle de la semana seleccionada */}
              <Box flex={1}>
                {selectedWeek && (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Semana del Lunes {formatDate(selectedWeek.weekStartDate)}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle1">Comentarios</Typography>
                    <List>
                      {selectedWeek.comments.map((c) => (
                        <React.Fragment key={c.id}>
                          <ListItem alignItems="flex-start">
                            <Avatar sx={{ mr: 1 }}>
                              {c.user.firstName[0]}{c.user.lastName[0]}
                            </Avatar>
                            <ListItemText
                              primary={c.content}
                              secondary={`${c.user.firstName} ${c.user.lastName} (${c.user.role}) - ${formatDate(c.createdAt)}`}
                            />
                          </ListItem>
                          {c.photos && c.photos.length > 0 && (
                            <Box sx={{ pl: 7, display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                              {c.photos.map((photoUrl, index) => (
                                <img 
                                  key={index} 
                                  src={getImageUrl(photoUrl, backendUrl)} 
                                  alt={`foto-comentario-${index}`} 
                                  style={{ height: '80px', width: 'auto', borderRadius: '4px' }}
                                />
                              ))}
                            </Box>
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                    {!selectedWeek.isCompleted && (
                      <Box mt={2} display="flex" flexDirection="column" gap={2}>
                        <TextField
                          label="Agregar comentario"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          multiline
                          rows={3}
                          variant="outlined"
                          fullWidth
                        />
                        <input
                          type="file"
                          multiple
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (e.target.files) {
                              setPhotosToUpload(Array.from(e.target.files));
                            }
                          }}
                          style={{ display: 'block', marginTop: '10px' }}
                        />
                        {/* Previsualización de fotos a subir */}
                        {photosToUpload.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {photosToUpload.map((file, index) => (
                              <Typography key={index} variant="caption">{file.name}</Typography>
                            ))}
                          </Box>
                        )}
                        <Button 
                          variant="contained" 
                          onClick={handleAddComment}
                          disabled={loading || (!newComment.trim() && photosToUpload.length === 0)}
                        >
                          {loading ? 'Guardando...' : 'Guardar Comentario'}
                        </Button>
                      </Box>
                    )}
                    <Box mt={2}>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={!canComplete()}
                        onClick={handleOpenModal}
                      >
                        Completar Semana
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          )}
        </>
      ) : (
        // Desktop: lista y detalle lado a lado
        <Box display="flex" gap={3}>
          {/* Lista de semanas */}
          <Paper sx={{ minWidth: 260, maxHeight: 600, overflow: 'auto' }}>
            <List>
              {weeks.map((week) => (
                <ListItem
                  key={week.id}
                  button
                  selected={(selectedWeek as Week | null)?.id === week.id}
                  onClick={() => handleSelectWeek(week)}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <ListItemText
                    primary={`Semana del Lunes ${formatDate(week.weekStartDate)}`}
                  />
                  <Chip
                    label={week.isCompleted ? 'Completada' : 'Pendiente'}
                    color={week.isCompleted ? 'success' : 'warning'}
                    size="small"
                    sx={{ ml: 2 }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          {/* Detalle de la semana seleccionada */}
          <Box flex={1}>
            {selectedWeek && (
              <>
                <Typography variant="h6" gutterBottom>
                  Semana del Lunes {formatDate(selectedWeek.weekStartDate)}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1">Comentarios</Typography>
                <List>
                  {selectedWeek.comments.map((c) => (
                    <React.Fragment key={c.id}>
                      <ListItem alignItems="flex-start">
                        <Avatar sx={{ mr: 1 }}>
                          {c.user.firstName[0]}{c.user.lastName[0]}
                        </Avatar>
                        <ListItemText
                          primary={c.content}
                          secondary={`${c.user.firstName} ${c.user.lastName} (${c.user.role}) - ${formatDate(c.createdAt)}`}
                        />
                      </ListItem>
                      {c.photos && c.photos.length > 0 && (
                        <Box sx={{ pl: 7, display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                          {c.photos.map((photoUrl, index) => (
                                                          <img 
                                key={index} 
                                src={getImageUrl(photoUrl, backendUrl)} 
                                alt={`foto-comentario-${index}`} 
                                style={{ height: '80px', width: 'auto', borderRadius: '4px' }}
                              />
                          ))}
                        </Box>
                      )}
                    </React.Fragment>
                  ))}
                </List>
                {!selectedWeek.isCompleted && (
                  <Box mt={2} display="flex" flexDirection="column" gap={2}>
                    <TextField
                      label="Agregar comentario"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      multiline
                      rows={3}
                      variant="outlined"
                      fullWidth
                    />
                    <input
                      type="file"
                      multiple
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.files) {
                          setPhotosToUpload(Array.from(e.target.files));
                        }
                      }}
                      style={{ display: 'block', marginTop: '10px' }}
                    />
                    {/* Previsualización de fotos a subir */}
                    {photosToUpload.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {photosToUpload.map((file, index) => (
                          <Typography key={index} variant="caption">{file.name}</Typography>
                        ))}
                      </Box>
                    )}
                    <Button 
                      variant="contained" 
                      onClick={handleAddComment}
                      disabled={loading || (!newComment.trim() && photosToUpload.length === 0)}
                    >
                      {loading ? 'Guardando...' : 'Guardar Comentario'}
                    </Button>
                  </Box>
                )}
                <Box mt={2}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={!canComplete()}
                    onClick={handleOpenModal}
                  >
                    Completar Semana
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Completar Semana: {selectedWeek ? formatDate(selectedWeek.weekStartDate) : ''}</DialogTitle>
        <DialogContent>
          <Typography variant="h6">Evaluación</Typography>
          <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
            {evaluationFields.map(field => (
              <Grid item xs={12} sm={6} key={field}>
                <TextField
                  fullWidth
                  label={field}
                  value={evaluation[field] || ''}
                  onChange={e => handleEvaluationChange(field, e.target.value)}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="h6" sx={{ mt: 3 }}>Firma</Typography>
          <Box sx={{ border: '1px solid black', mt: 1, width: '100%', height: 200 }}>
            <SignatureCanvas
              ref={sigCanvasRef}
              penColor='black'
              canvasProps={{ style: { width: '100%', height: '100%' } }}
            />
          </Box>
          <Button onClick={handleClearSignature} sx={{ mt: 1 }}>Limpiar</Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSaveSignature} variant="outlined">Guardar Firma</Button>
          <Button onClick={handleCompleteWeekWithEvaluation} variant="contained" color="primary">Confirmar Completado</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScheduledServiceDetail; 